import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/page-header";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";

export default function ProjectNewPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [users, setUsers] = useState<{ id: string; name: string }[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: "", pmId: "", kickoffDate: "", status: "PLANNING",
  });

  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data.data ?? []));
  }, []);

  function set<K extends keyof typeof form>(k: K, v: (typeof form)[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const payload: any = { name: form.name, status: form.status };
      if (form.pmId)        payload.pmId = form.pmId;
      if (form.kickoffDate) payload.kickoffDate = form.kickoffDate;
      await api.post("/projects", payload);
      qc.invalidateQueries({ queryKey: ["/projects"] });
      navigate("/projects");
    } catch (err: any) {
      setError(err?.response?.data?.message ?? "Failed to create project.");
    } finally { setBusy(false); }
  }

  return (
    <>
      <PageHeader title="New Project" description="Usually auto-created when a deal closes won — but you can also add manually." />
      <form onSubmit={submit} className="p-6">
        <Card className="max-w-xl">
          <CardContent className="grid gap-4 p-6">
            <div>
              <Label htmlFor="name">Name *</Label>
              <Input id="name" required value={form.name} onChange={(e) => set("name", e.target.value)} />
            </div>
            <div>
              <Label htmlFor="pmId">Project Manager</Label>
              <Select id="pmId" value={form.pmId} onChange={(e) => set("pmId", e.target.value)}>
                <option value="">— Unassigned —</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </Select>
            </div>
            <div>
              <Label htmlFor="kickoffDate">Kickoff date</Label>
              <Input id="kickoffDate" type="date" value={form.kickoffDate} onChange={(e) => set("kickoffDate", e.target.value)} />
            </div>
            <div>
              <Label htmlFor="status">Status</Label>
              <Select id="status" value={form.status} onChange={(e) => set("status", e.target.value)}>
                <option value="PLANNING">Planning</option>
                <option value="ACTIVE">Active</option>
                <option value="ON_HOLD">On hold</option>
                <option value="COMPLETED">Completed</option>
                <option value="CANCELLED">Cancelled</option>
              </Select>
            </div>
          </CardContent>
          <div className="flex items-center justify-between border-t px-6 py-4">
            {error ? <p className="text-sm text-destructive">{error}</p> : <span />}
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => navigate("/projects")}>Cancel</Button>
              <Button type="submit" disabled={busy || !form.name}>{busy ? "Saving…" : "Create"}</Button>
            </div>
          </div>
        </Card>
      </form>
    </>
  );
}
